import * as CL from '@design-system/component-library';
import { useSearchParams } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables.js';
import {
  attentionMsg,
  employerCompanyMsg,
  formatDate,
  greetingNoNameMsg,
  hereMsg,
  invitationLandingAttentionItem1Msg,
  invitationLandingAttentionItem2Msg,
  invitationLandingAttentionItem3AdminMsg,
  invitationLandingAttentionItem3EmployeeMsg,
  invitationLandingCreateElisaIdMsg,
  invitationLandingInstructionsPart1AdminMsg,
  invitationLandingInstructionsPart1EmployeeMsg,
  invitationLandingInstructionsPart2AdminMsg,
  invitationLandingIntroAdminMsg,
  invitationLandingIntroEmployeeMsg,
  invitationLandingLinkValidMsg,
  invitationLandingProceedAuthMsg,
  invitationLandingTitleAdminMsg,
  invitationLandingTitleEmployeeMsg,
  signInMsg,
  t,
} from '../../common/i18n/index.js';
import { type InvitationLanding, InvitationProfileEnum } from '../../generated/api/models.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import type { JSX } from 'react';

import './InvitationLandingPage.scss';

export interface InvitationLandingPageProps {
  invitationData: InvitationLanding;
}

export const InvitationLandingPage = ({ invitationData }: InvitationLandingPageProps): JSX.Element => {
  const [searchParams] = useSearchParams();
  const { prepareElisaIdLogin } = useAuth();
  const invitationId = searchParams.get('invitationid');
  const isEmployee = invitationData.profile === InvitationProfileEnum.CUSTOMER_EMPLOYEE;

  const handlePrepareElisaIdLogin = () => {
    prepareElisaIdLogin({
      invitationId: invitationId ?? undefined,
      isEmployee,
    });
  };

  const expireDate = formatDate(invitationData.expiresAt) || '';
  const expiresText = t.GBFN('{}', expireDate);

  return (
    <div className="ds-container">
      <div className={`of-invitation-landing__body ${dsClass.PADDING_7}`}>
        <CL.Grid>
          <CL.GridRow>
            <CL.GridCol colWidthXS={4} colWidthS={8} colWidthM={6} colWidthL={7}>
              <div className={`${dsClass.PADDING_0}`}>
                <h1 className={`${dsClass.MARGIN_TOP_2}`}>
                  {isEmployee ? t.QU5I(invitationLandingTitleEmployeeMsg) : t.QU5H(invitationLandingTitleAdminMsg)}
                </h1>

                <div className={`${dsClass.MARGIN_BOTTOM_4}`}>
                  <p className={`${dsClass.FONT_WEIGHT_BOLD}`}>
                    {t.UC34(greetingNoNameMsg)},<br></br>
                    {isEmployee ? t.KFUI(invitationLandingIntroEmployeeMsg) : t.KFUH(invitationLandingIntroAdminMsg)}
                    <br></br>
                  </p>
                  <p>
                    <span>{t.XSEI(invitationLandingLinkValidMsg)}</span>{' '}
                    <span className={`${dsClass.FONT_WEIGHT_700}`}>{expiresText}</span>
                    <br></br>
                    <span>{t.Q8B7(employerCompanyMsg)}:</span>{' '}
                    <span className={`${dsClass.FONT_WEIGHT_700}`}>{invitationData.companyName}</span>
                  </p>
                  <p>
                    {isEmployee
                      ? t.JSLD(invitationLandingInstructionsPart1EmployeeMsg)
                      : t.KXYE(invitationLandingInstructionsPart1AdminMsg)}
                  </p>
                </div>

                {!isEmployee && (
                  <p className={`${dsClass.MARGIN_TOP_4} ${dsClass.MARGIN_BOTTOM_0}`}>
                    {t.OP1P(invitationLandingInstructionsPart2AdminMsg)}{' '}
                    <CL.Link linkHref={paths.AUTHENTICATION_EXT} linkTarget="_blank">
                      {t.OP1Q(hereMsg)}
                    </CL.Link>
                    .
                  </p>
                )}

                <div className={`of-invitation-landing__buttons ${dsClass.DISPLAY_FLEX} ${dsClass.MARGIN_VERTICAL_4}`}>
                  <CL.Button color="primary" size="l" onClick={handlePrepareElisaIdLogin}>
                    {t.Z16I(signInMsg)}
                  </CL.Button>
                  <CL.Button color="secondary" size="l" onClick={handlePrepareElisaIdLogin}>
                    {isEmployee ? t.TXT7(invitationLandingCreateElisaIdMsg) : t.H3KG(invitationLandingProceedAuthMsg)}
                  </CL.Button>
                </div>

                <div>
                  <p className={`${dsClass.MARGIN_0}`}>
                    <b>{t.W4RE(attentionMsg)}:</b>
                  </p>
                  <CL.List
                    listType="unordered"
                    items={
                      isEmployee
                        ? [
                            t.VX8X(invitationLandingAttentionItem1Msg),
                            t.S06E(invitationLandingAttentionItem2Msg),
                            t.YJIG(invitationLandingAttentionItem3EmployeeMsg),
                          ]
                        : [
                            t.VX8X(invitationLandingAttentionItem1Msg),
                            t.S06E(invitationLandingAttentionItem2Msg),
                            t.YJIF(invitationLandingAttentionItem3AdminMsg),
                          ]
                    }
                  />
                </div>
              </div>
            </CL.GridCol>

            <CL.GridCol colWidthXS={4} colWidthS={8} colWidthM={6} colWidthL={5}>
              <div
                className={`of-invitation-landing__illustration ${dsClass.DISPLAY_FLEX} ${dsClass.JUSTIFY_CONTENT_CENTER} ${dsClass.ALIGN_ITEMS_CENTER} ${dsClass.PADDING_VERTICAL_4} ${dsClass.PADDING_HORIZONTAL_0} ${isEmployee ? 'of-invitation-landing__illustration--employee' : 'of-invitation-landing__illustration--admin'}`}
              />
            </CL.GridCol>
          </CL.GridRow>
        </CL.Grid>
      </div>
    </div>
  );
};
